import { useEffect, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { emitEvent } from "../src/events/eventBus";
import { useInteractionStore } from "../src/stores/useInteraction";

export default function CakeTrigger({
  targetRef,
  cakePosition,
  focusPosition,
  radius = 30,
  cameraOffset = [4, 4, 6],
}) {
  const triggeredRef = useRef(false);
  const cakeVector = useRef(new THREE.Vector3(...cakePosition));
  const focusVector = useRef(new THREE.Vector3(...focusPosition));
  const cameraPositionVector = useRef(new THREE.Vector3(...cameraOffset));
  const resetTimeoutRef = useRef(null);

  useEffect(() => {
    cakeVector.current.set(...cakePosition);
  }, [cakePosition]);

  useEffect(() => {
    focusVector.current.set(...focusPosition);
  }, [focusPosition]);

  useEffect(() => {
    cameraPositionVector.current.set(...cameraOffset);
  }, [cameraOffset]);

  useEffect(() => {
    return () => {
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current);
        resetTimeoutRef.current = null;
      }
    };
  }, []);

  useFrame(() => {
    if (triggeredRef.current) return;

    const controller = targetRef?.current?.group;
    if (!controller || typeof controller.translation !== "function") return;

    const translation = controller.translation();
    if (!translation) return;

    const dx = translation.x - cakeVector.current.x;
    const dy = translation.y - cakeVector.current.y;
    const dz = translation.z - cakeVector.current.z;
    const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);

    if (distance > radius) return;

    triggeredRef.current = true;

    const state = useInteractionStore.getState();
    useInteractionStore.setState({ movementLocked: true, playerFrozen: true });

    const cameraPos = new THREE.Vector3().copy(cameraPositionVector.current);

    state.setCameraOverride(true, {
      position: [cameraPos.x, cameraPos.y, cameraPos.z],
      target: [focusVector.current.x, focusVector.current.y, focusVector.current.z],
      transitionDuration: 0.3,
    });

    emitEvent("playCake");

    if (resetTimeoutRef.current) {
      clearTimeout(resetTimeoutRef.current);
    }

    resetTimeoutRef.current = window.setTimeout(() => {
      useInteractionStore.setState({ movementLocked: false, playerFrozen: false });
      const releaseState = useInteractionStore.getState();
      releaseState.setCameraOverride(false);
    }, 5000);
  });

  return null;
}
